import { useRef, useState, useEffect, useCallback } from "react";
import type { AnimationControls } from "framer-motion";

interface PlayerSnapshot {
  username: string;
  status: string;
  action_cards: Array<{ subtype: string | null }>;
}

interface ActionEvent {
  action: string;
  attacker: string;
  target: string;
}

interface GameStateSnapshot {
  turn_state: string;
  current_player: string;
  pending_action: { type: string; value: number | null; subtype: string | null } | null;
  players: PlayerSnapshot[];
  turn_index: number;
  player_order: string[];
  last_action_event: ActionEvent | null;
}

export interface AlertData {
  cardType: string;
  attackerName: string;
  isSelfTargeted: boolean;
}

const FLASH_COLORS: Record<string, string> = {
  freeze: "rgba(96,165,250,0.15)",
  flip_three: "rgba(248,113,113,0.15)",
  second_chance: "rgba(251,191,36,0.15)",
};

const SHAKE_CONFIG: Record<string, { maxOffset: number; duration: number }> = {
  freeze: { maxOffset: 2, duration: 0.3 },
  flip_three: { maxOffset: 3, duration: 0.4 },
};

function generateShakeKeyframes(maxOffset: number): number[] {
  const steps = 8;
  const values = Array.from({ length: steps }, () =>
    (Math.random() - 0.5) * 2 * maxOffset
  );
  values.push(0);
  return values;
}

function triggerEffects(
  cardSubtype: string,
  setFlashColor: (c: string | null) => void,
  shakeControls: AnimationControls,
) {
  setFlashColor(FLASH_COLORS[cardSubtype] ?? null);
  setTimeout(() => setFlashColor(null), 500);

  const shake = SHAKE_CONFIG[cardSubtype];
  if (shake) {
    const isMobile = window.innerWidth < 768;
    const offset = isMobile ? shake.maxOffset / 2 : shake.maxOffset;

    shakeControls.start({
      x: generateShakeKeyframes(offset),
      y: generateShakeKeyframes(offset),
      transition: { duration: shake.duration, ease: "easeOut" },
    });
  }
}

export function useActionCardNotification({
  gameState,
  currentUsername,
  shakeControls,
}: {
  gameState: GameStateSnapshot | null;
  currentUsername: string;
  shakeControls: AnimationControls;
}) {
  const prevStateRef = useRef<GameStateSnapshot | null>(null);
  const [alertData, setAlertData] = useState<AlertData | null>(null);
  const [flashColor, setFlashColor] = useState<string | null>(null);
  const prevActionEventKeyRef = useRef<string | null>(null);

  let suspenseAttacker: string | null = null;
  let pendingCardType: string | null = null;

  if (
    gameState &&
    gameState.turn_state === "waiting_for_target" &&
    gameState.pending_action &&
    gameState.current_player !== currentUsername
  ) {
    pendingCardType = gameState.pending_action.subtype;
    suspenseAttacker = gameState.current_player;
  }

  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = gameState;

    if (!prev || !gameState) {
      if (gameState?.last_action_event) {
        const key = `${gameState.last_action_event.action}:${gameState.last_action_event.attacker}:${gameState.last_action_event.target}:${gameState.turn_index}`;
        prevActionEventKeyRef.current = key;
      }
      return;
    }

    // Detect auto-resolved actions via last_action_event
    const actionEvent = gameState.last_action_event;
    const actionEventKey = actionEvent
      ? `${actionEvent.action}:${actionEvent.attacker}:${actionEvent.target}:${gameState.turn_index}`
      : null;
    const isNewEvent = actionEventKey !== null && actionEventKey !== prevActionEventKeyRef.current;
    prevActionEventKeyRef.current = actionEventKey;

    if (isNewEvent && actionEvent && actionEvent.target === currentUsername) {
      const isSelf = actionEvent.attacker === currentUsername;
      setAlertData({ cardType: actionEvent.action, attackerName: actionEvent.attacker, isSelfTargeted: isSelf });
      triggerEffects(actionEvent.action, setFlashColor, shakeControls);
      const timer = setTimeout(() => setAlertData(null), 4500);
      return () => clearTimeout(timer);
    }

    // Detect manually resolved actions via state transition from waiting_for_target
    if (prev.turn_state !== "waiting_for_target") return;
    if (gameState.turn_state === "waiting_for_target") return;

    const cardSubtype = prev.pending_action?.subtype;
    const attackerName = prev.current_player;
    if (!cardSubtype) return;

    let targetUsername: string | null = null;

    if (cardSubtype === "flip_three") {
      targetUsername = gameState.current_player;
    } else {
      const prevActionCounts = new Map(
        prev.players.map((p) => [p.username, p.action_cards.length])
      );
      for (const player of gameState.players) {
        const prevLen = prevActionCounts.get(player.username) ?? 0;
        if (player.action_cards.length > prevLen) {
          targetUsername = player.username;
          break;
        }
      }
    }

    if (targetUsername !== currentUsername) return;

    const isSelf = attackerName === currentUsername;
    setAlertData({ cardType: cardSubtype, attackerName, isSelfTargeted: isSelf });
    triggerEffects(cardSubtype, setFlashColor, shakeControls);

    const timer = setTimeout(() => setAlertData(null), 4500);
    return () => clearTimeout(timer);
  }, [gameState, currentUsername, shakeControls]);

  const dismissAlert = useCallback(() => setAlertData(null), []);

  return {
    suspenseAttacker,
    pendingCardType,
    alertData,
    flashColor,
    dismissAlert,
  };
}
